import {useMemo} from "react";
import {
    RequestFilter,
    RequestOverviewRowDTO,
    RequestSearchParamsDTO,
    RequestSortField,
    RequestTableViewStateDTO
} from "@/api/data-contracts";
import {Constants} from "@/api/constants";
import {requestApi, tableViewStateApi} from "@/boxhub-api";
import {initialQuoteStatuses, initialRequestStatuses} from "@/datacaches";
import {
    OverviewSearchResult,
    OverviewState,
    OverviewStateAdapter,
    REQUEST_SIZE_OPTIONS,
    SortDirection
} from "@/common/overview-state/overview-state-model";
import {
    requestDocumentToFilters,
    RequestOverviewFilters,
    requestStateToDocument,
    requestStateToSearchParams
} from "@/common/overview-state/request-view-state";
import {useOverviewTableState} from "@/common/overview-state/useOverviewTableState";

/** Return shape of `useRequestOverviewState` — forwards the generic hook, plus paging convenience fields. */
export interface UseRequestOverviewStateResult {
    readonly applied: OverviewState<RequestOverviewFilters, RequestSortField>;
    readonly rows: RequestOverviewRowDTO[];
    readonly totalElements: number;
    readonly totalPages: number;
    readonly totalUnreadMessages: number;
    readonly page: number;
    readonly size: number;
    readonly defaults: RequestOverviewFilters;
    readonly defaultSize: number;
    /** Page-size choices for the size selector (Phase 1.3) — `Constants.REQUEST_SEARCH_SIZE_OPTIONS`. */
    readonly sizeOptions: readonly number[];
    resetAll(): void;
    setSearchText(text: string): void;
    setSort(field: RequestSortField | undefined, direction: SortDirection | undefined): void;
    setSize(size: number): void;
    setPage(page: number): void;
    /** Sofortwirkung (Phase 2.2, Standing constraint 4): merges into `applied.filters` immediately. */
    setFilters(partial: Partial<RequestOverviewFilters>): void;
}

/**
 * Reads the stored view state of one request-search variant. Resolves to `undefined` on the 204-empty-body
 * case: Axios represents a bodyless 204 as an empty string in `response.data`, so a truthiness check on the
 * document alone is not enough (see the plan's "Standing constraints"; pattern mirrored from
 * `conversationApi.ts#fetchConversation`).
 */
async function loadRequestDocument(variant: RequestFilter): Promise<RequestTableViewStateDTO | undefined> {
    const response = await tableViewStateApi.getRequestState(variant);
    const raw: unknown = response.data;
    if (response.status === 204 || raw === "" || raw == null) {
        return undefined;
    }
    return raw as RequestTableViewStateDTO;
}

/** "Everything selected" for one request-search variant (Phase 0, Entscheidung 3 — `DRAFT` included). */
function buildDefaultFilters(variant: RequestFilter): RequestOverviewFilters {
    return variant === RequestFilter.INTERNAL
        ? {requestStatuses: [...initialRequestStatuses]}
        : {quoteStatuses: [...initialQuoteStatuses]};
}

/**
 * Runs the request search and maps the response onto `OverviewSearchResult`: rows fall back to an empty
 * list, `page`/`size` fall back to the request parameters, and `totalElements`/`totalPages`/
 * `totalUnreadMessages` fall back to 0 — all for a response that omits them.
 */
async function searchRequestRows(
    params: RequestSearchParamsDTO
): Promise<OverviewSearchResult<RequestOverviewRowDTO>> {
    const response = await requestApi.searchRequests(params);
    const data = response.data;
    return {
        rows: data.rows ?? [],
        page: data.page ?? params.page ?? 0,
        size: data.size ?? params.size ?? Constants.REQUEST_SEARCH_SIZE_DEFAULT,
        totalElements: data.totalElements ?? 0,
        totalPages: data.totalPages ?? 0,
        totalUnreadMessages: data.totalUnreadMessages ?? 0
    };
}

/**
 * Request-search adapter for `useOverviewTableState` (Phase 1.3), parametrized over one
 * `RequestFilter` variant (INTERNAL or EXTERNAL). Carries all variant-specific knowledge — which four
 * endpoints back the variant, what "everything selected" means, how the document and search parameters
 * map onto the generic state (`request-view-state.ts`, Phase 1.2) — and contains no state logic of its
 * own (no draft/applied split, no debouncing, no page clamping, no error handling): all of that lives in
 * the generic hook.
 */
export function useRequestOverviewState(variant: RequestFilter): UseRequestOverviewStateResult {
    const adapter = useMemo<
        OverviewStateAdapter<
            RequestTableViewStateDTO,
            RequestOverviewRowDTO,
            RequestOverviewFilters,
            RequestSearchParamsDTO,
            RequestSortField
        >
    >(
        () => ({
            loadDocument: () => loadRequestDocument(variant),
            saveDocument: async (doc) => {
                await tableViewStateApi.putRequestState(variant, doc);
            },
            clearDocument: async () => {
                await tableViewStateApi.deleteRequestState(variant);
            },
            search: searchRequestRows,
            defaults: () => buildDefaultFilters(variant),
            defaultSize: () => Constants.REQUEST_SEARCH_SIZE_DEFAULT,
            toDocument: requestStateToDocument,
            fromDocument: (doc, defaults) => requestDocumentToFilters(doc, variant, defaults),
            toSearchParams: (state) => requestStateToSearchParams(state, variant)
        }),
        [variant]
    );

    const state = useOverviewTableState(adapter);

    // `state` is already referentially stable (memoized in `useOverviewTableState`); spreading it into a
    // plain object literal on every call would throw that stability away again, so the paging convenience
    // fields are added inside a `useMemo` keyed on `state` itself instead.
    return useMemo(
        () => ({
            ...state,
            page: state.applied.page,
            size: state.applied.size,
            sizeOptions: REQUEST_SIZE_OPTIONS
        }),
        [state]
    );
}
